import { yupResolver } from "@hookform/resolvers/yup";
import { AuthStatus, useAppStatus, ApiError } from "@wexinc/wex-common-web";
import { Button, Form, Heading, TextField, WexLogo } from "@wexinc/wex-ui";
import { CopRoutes } from "app/routes";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreateLocalInvitationUser, useCopSecurityService } from "services/security";
import useInvitationService from "services/security/useInvitationService";
import * as yup from "yup";
import PasswordConfirmPassword, {
  getSchema as getPasswordConfirmPasswordSchema,
} from "../components/PasswordConfirmPassword";

interface RegistrationSchema {
  firstName: string;
  lastName: string;
}

const registrationFormSchema = yup.object<RegistrationSchema>({
  firstName: yup.string().trim().required("First name is required."),
  lastName: yup.string().trim().required("Last name is required."),
});

interface FormParams {
  firstName: string;
  lastName: string;
  password?: string;
}

const Registration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const copSecurityService = useCopSecurityService();
  const email = searchParams.get("email");
  const isIdpUser = useRef(false);
  const [formIsReady, setFormIsReady] = useState(false);
  const invitationService = useInvitationService();
  const invitationId = invitationService.getInvitationId();
  const securityToken = copSecurityService.getSecurityTokenFromUrl();
  const { showBoundary } = useErrorBoundary();
  const appStatus = useAppStatus();
  const defaultValues = useRef<{ [x: string]: unknown }>({});
  const schema = useRef(registrationFormSchema);
  const navigate = useNavigate();
  const [knownErrorMessage, setKnownErrorMessage] = useState<string>();
  const [knownErrorCode, setKnownErrorCode] = useState<string>();
  const isComplete = useRef(false);

  if (isComplete.current === false) {
    if (!email) {
      throw Error("Cannot locate email in registration form.");
    }

    if (!invitationId) {
      throw Error("No invitation id found.");
    }
  }

  const actionableAuthStatuses: AuthStatus[] = ["authenticated", "not-authenticated"];

  async function handleSubmit(formParams: FormParams) {
    appStatus.setGlobalMessage("Completing registration...");
    if (isIdpUser.current === false) {
      try {
        await invitationService.createLocalUser(invitationId!, securityToken, formParams as CreateLocalInvitationUser);
      } catch (e) {
        if (e instanceof ApiError && e.errorCodes?.some((err) => err === "E-COP-2024")) {
          // The user already exists in Okta. This happens if they are invited out of order, or if they're already 
          // in the okta instance for a purpose other than COP access.
          appStatus.setGlobalMessage(undefined);
          isComplete.current = true;
          setKnownErrorCode("E-COP-2024")
          setKnownErrorMessage(
            "A user with this email already exists in the COP Identity Provider service. " +
            "To continue the registration process, simply login to COP using your IdP credentials. " +
            "If you're unsure of your credentials, please reach out to a COP Identity " +
            "Provider administrator to reset your password."
          );
          return;
        }
      }
      copSecurityService.loginViaUsernamePassword(
        {
          username: email!,
          password: formParams.password!,
        },
        window.origin,
      );
    } else {
      try {
        await copSecurityService.updateCurrentUser({
          firstName: formParams.firstName,
          lastName: formParams.lastName,
        });
        isComplete.current = true;
        navigate(CopRoutes.root);
      } catch (error) {
        appStatus.setGlobalMessage(undefined);
        showBoundary(error);
      }
    }
  }

  useEffect(
    function determineIfIdpUserAndSetupForm() {
      if (actionableAuthStatuses.indexOf(copSecurityService.securityStore?.authStatus) !== -1) {
        isIdpUser.current = copSecurityService.securityStore?.authStatus === "authenticated";
        if (isIdpUser.current === false) {
          schema.current = schema.current.concat(getPasswordConfirmPasswordSchema(email!));
        } else {
          if (copSecurityService.securityStore.loggedInUser?.email === email) {
            defaultValues.current = {
              firstName: copSecurityService.securityStore.loggedInUser?.firstName,
              lastName: copSecurityService.securityStore.loggedInUser?.lastName,
            };
          } else {
            isComplete.current = true;
            // Mismatch: Means the user was logged-in/authenticated already with an account different than the one in the invitation.
            setKnownErrorMessage(
              `Uh oh. You are already logged into COP with the user id of ${copSecurityService.securityStore.loggedInUser?.email}. Please log out of COP with that account.`,
            );
          }
        }
        appStatus.setGlobalMessage(undefined);
        setFormIsReady(true);
      }
    },
    [copSecurityService.securityStore],
  );

  return (
    <div className="h-full overflow-auto relative">
      <div className="h-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 max-h-[750px]">
        <section className={classnames("flex flex-col", !knownErrorMessage ? "w-[400px]" : "")}>
          <WexLogo
            height="40px"
            width="124px"
          />
          <div className="pb-10 pt-2 text-neutral-dark-60 text-lg-plus font-semibold">Customer Operations Portal</div>

          {formIsReady && !knownErrorMessage && (
            <>
              <Heading
                size="medium"
                className="pb-4"
              >
                Registration form
              </Heading>
              <Form
                onSubmit={handleSubmit}
                options={{
                  defaultValues: defaultValues.current,
                  criteriaMode: "all",
                  mode: "onSubmit",
                  resolver: yupResolver(schema.current, {
                    abortEarly: false,
                  }),
                }}
              >
                <>
                  <TextField
                    name="firstName"
                    label="First name"
                  />
                  <TextField
                    name="lastName"
                    label="Last name"
                  />
                  {isIdpUser.current === false && <PasswordConfirmPassword username={email!} />}
                  <Button
                    id="login-submit"
                    className="w-full !h-14 mt-10"
                    testId="login-button"
                    type="submit"
                  >
                    Complete Registration
                  </Button>
                </>
              </Form>
            </>
          )}
          {knownErrorMessage && (
            <>
              <Heading
                size="medium"
                className="text-primary-40"
              >
                {knownErrorMessage}
              </Heading>
              {knownErrorCode === "E-COP-2024" ? (
                <Button
                  className="!h-14 mt-10 w-40"
                  testId="go-to-cop"
                  buttonType="primary"
                  onClick={() => navigate(CopRoutes.loginRoot)}
                >
                  Login
                </Button>
              ) : (
                <Button
                  className="!h-14 mt-10 w-40"
                  testId="go-to-cop"
                  buttonType="primary"
                  onClick={() => navigate(CopRoutes.root)}
                >
                  Go to COP
                </Button>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Registration;
